import React, { useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import { AiUsageByDayDto, AiUsageByProviderDto, AiUsageSummaryDto, AiUsageByFeatureDto } from './AiManagementPage';
import AiAnalyticsInsightCards from './AiAnalyticsInsightCards';
import AiUsageChart from './AiUsageChart';

interface AiUsageAnalyticsPanelProps {
    summary: AiUsageSummaryDto | null;
    loading: boolean;
    fromDate: string;
    toDate: string;
    onDateChange: (from: string, to: string) => void;
}

type Order = 'asc' | 'desc';

const AiUsageAnalyticsPanel: React.FC<AiUsageAnalyticsPanelProps> = ({
    summary,
    loading,
    fromDate,
    toDate,
    onDateChange,
}) => {
    const [providerOrderBy, setProviderOrderBy] = useState<keyof AiUsageByProviderDto>('CostUsd');
    const [providerOrder, setProviderOrder] = useState<Order>('desc');
    const [dayOrderBy, setDayOrderBy] = useState<keyof AiUsageByDayDto>('Date');
    const [dayOrder, setDayOrder] = useState<Order>('desc');
    const [featureOrderBy, setFeatureOrderBy] = useState<keyof AiUsageByFeatureDto>('CostUsd');
    const [featureOrder, setFeatureOrder] = useState<Order>('desc');

    const handleProviderSort = (property: keyof AiUsageByProviderDto) => {
        const isAsc = providerOrderBy === property && providerOrder === 'asc';
        setProviderOrder(isAsc ? 'desc' : 'asc');
        setProviderOrderBy(property);
    };

    const handleDaySort = (property: keyof AiUsageByDayDto) => {
        const isAsc = dayOrderBy === property && dayOrder === 'asc';
        setDayOrder(isAsc ? 'desc' : 'asc');
        setDayOrderBy(property);
    };

    const handleFeatureSort = (property: keyof AiUsageByFeatureDto) => {
        const isAsc = featureOrderBy === property && featureOrder === 'asc';
        setFeatureOrder(isAsc ? 'desc' : 'asc');
        setFeatureOrderBy(property);
    };

    const handleQuickRange = (days: number) => {
        const to = new Date();
        const from = new Date();
        from.setDate(to.getDate() - days);
        onDateChange(from.toISOString().split('T')[0], to.toISOString().split('T')[0]);
    };

    // Sorting Top Providers
    const sortedProviders = React.useMemo(() => {
        if (!summary?.TopProviders) return [];
        return [...summary.TopProviders].sort((a, b) => {
            const valA = a[providerOrderBy];
            const valB = b[providerOrderBy];
            if (valA === undefined || valB === undefined) return 0;
            
            let comparison = 0;
            if (typeof valA === 'string' && typeof valB === 'string') {
                comparison = valA.localeCompare(valB);
            } else {
                comparison = (valA as number) - (valB as number);
            }
            return providerOrder === 'desc' ? -comparison : comparison;
        });
    }, [summary?.TopProviders, providerOrderBy, providerOrder]);

    // Sorting Daily Breakdown
    const sortedDaily = React.useMemo(() => {
        if (!summary?.DailyBreakdown) return [];
        return [...summary.DailyBreakdown].sort((a, b) => {
            const valA = a[dayOrderBy];
            const valB = b[dayOrderBy];
            if (valA === undefined || valB === undefined) return 0;

            let comparison = 0;
            if (dayOrderBy === 'Date') {
                comparison = new Date(valA as string).getTime() - new Date(valB as string).getTime();
            } else {
                comparison = (valA as number) - (valB as number);
            }
            return dayOrder === 'desc' ? -comparison : comparison;
        });
    }, [summary?.DailyBreakdown, dayOrderBy, dayOrder]);

    // Sorting Feature Breakdown
    const sortedFeatures = React.useMemo(() => {
        if (!summary?.ByFeature) return [];
        return [...summary.ByFeature].sort((a, b) => {
            const valA = a[featureOrderBy];
            const valB = b[featureOrderBy];
            if (valA === undefined || valB === undefined) return 0;

            let comparison = 0;
            if (typeof valA === 'string' && typeof valB === 'string') {
                comparison = valA.localeCompare(valB);
            } else {
                comparison = (valA as number) - (valB as number);
            }
            return featureOrder === 'desc' ? -comparison : comparison;
        });
    }, [summary?.ByFeature, featureOrderBy, featureOrder]);



    return (
        <Stack spacing={3}>
            {/* Filter controls */}
            <Paper
                elevation={3}
                sx={{
                    p: 3,
                    borderRadius: 3,
                    background: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h6" fontWeight="600" color="textPrimary">
                            Usage Filter
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Select date range or quick presets to view usage analytics
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="From"
                                type="date"
                                value={fromDate}
                                onChange={(e) => onDateChange(e.target.value, toDate)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                            />
                            <TextField
                                label="To"
                                type="date"
                                value={toDate}
                                onChange={(e) => onDateChange(fromDate, e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                            />
                        </Stack>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                            <Button size="small" variant="outlined" onClick={() => handleQuickRange(7)}>
                                7 Days
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => handleQuickRange(30)}>
                                30 Days
                            </Button>
                            <Button size="small" variant="outlined" onClick={() => handleQuickRange(90)}>
                                90 Days
                            </Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {/* Computed Insight Cards */}
            <Box>
                <Typography variant="h6" fontWeight="600" color="textPrimary" mb={0.5}>
                    Period Insights
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                    Computed metrics for the selected date range — distinct from the 30-day overview
                </Typography>
                <AiAnalyticsInsightCards summary={summary} loading={loading} />
            </Box>

            {/* Usage charts */}
            {!loading && summary && (
                <AiUsageChart dailyData={summary.DailyBreakdown} modelData={summary.ByModel} />
            )}

            {/* Detailed tables */}
            <Grid container spacing={3}>
                {/* Top Providers Table */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <Typography variant="h6" fontWeight="600" color="textPrimary" mb={2}>
                            Top Providers
                        </Typography>
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={providerOrderBy === 'ProviderName'}
                                                direction={providerOrderBy === 'ProviderName' ? providerOrder : 'asc'}
                                                onClick={() => handleProviderSort('ProviderName')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Name
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={providerOrderBy === 'RoleName'}
                                                direction={providerOrderBy === 'RoleName' ? providerOrder : 'asc'}
                                                onClick={() => handleProviderSort('RoleName')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Role
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={providerOrderBy === 'Requests'}
                                                direction={providerOrderBy === 'Requests' ? providerOrder : 'asc'}
                                                onClick={() => handleProviderSort('Requests')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Requests
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={providerOrderBy === 'TotalTokens'}
                                                direction={providerOrderBy === 'TotalTokens' ? providerOrder : 'asc'}
                                                onClick={() => handleProviderSort('TotalTokens')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Tokens
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={providerOrderBy === 'CostUsd'}
                                                direction={providerOrderBy === 'CostUsd' ? providerOrder : 'asc'}
                                                onClick={() => handleProviderSort('CostUsd')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Cost
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedProviders.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center">
                                                No providers recorded during this period.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedProviders.map((p) => (
                                            <TableRow
                                                key={p.ProviderId}
                                                hover
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell sx={{ fontWeight: '500' }}>{p.ProviderName || 'Unknown Provider'}</TableCell>
                                                <TableCell sx={{ color: 'text.secondary' }}>{p.RoleName || '-'}</TableCell>
                                                <TableCell align="right">{p.Requests.toLocaleString()}</TableCell>
                                                <TableCell align="right">{p.TotalTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right">${(p.CostUsd || 0).toFixed(4)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Daily Breakdown Table */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Paper
                        elevation={3}
                        sx={{
                            p: 3,
                            borderRadius: 3,
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(8px)',
                            border: '1px solid rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <Typography variant="h6" fontWeight="600" color="textPrimary" mb={2}>
                            Daily Breakdown
                        </Typography>
                        <TableContainer sx={{ maxHeight: 400 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={dayOrderBy === 'Date'}
                                                direction={dayOrderBy === 'Date' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('Date')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Date
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={dayOrderBy === 'Requests'}
                                                direction={dayOrderBy === 'Requests' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('Requests')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Requests
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={dayOrderBy === 'PromptTokens'}
                                                direction={dayOrderBy === 'PromptTokens' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('PromptTokens')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Prompt
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={dayOrderBy === 'CompletionTokens'}
                                                direction={dayOrderBy === 'CompletionTokens' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('CompletionTokens')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Completion
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={dayOrderBy === 'TotalTokens'}
                                                direction={dayOrderBy === 'TotalTokens' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('TotalTokens')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Total Tokens
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell align="right">
                                            <TableSortLabel
                                                active={dayOrderBy === 'CostUsd'}
                                                direction={dayOrderBy === 'CostUsd' ? dayOrder : 'asc'}
                                                onClick={() => handleDaySort('CostUsd')}
                                                style={{ fontWeight: 600 }}
                                            >
                                                Cost
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedDaily.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                No daily logs found during this period.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedDaily.map((d) => (
                                            <TableRow
                                                key={d.Date}
                                                hover
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell sx={{ fontWeight: '500' }}>
                                                    {new Date(d.Date).toLocaleDateString(undefined, {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        timeZone: 'UTC',
                                                    })}
                                                </TableCell>
                                                <TableCell align="right">{d.Requests.toLocaleString()}</TableCell>
                                                <TableCell align="right">{d.PromptTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right">{d.CompletionTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right">{d.TotalTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right">${(d.CostUsd || 0).toFixed(4)}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Feature Breakdown Table */}
            {summary?.ByFeature && summary.ByFeature.length > 0 && (
                <Grid container spacing={3}>
                    <Grid size={12}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(8px)',
                                border: '1px solid rgba(0, 0, 0, 0.08)',
                            }}
                        >
                            <Typography variant="h6" fontWeight="600" color="textPrimary" mb={2}>
                                Feature Breakdown
                            </Typography>
                            <TableContainer sx={{ maxHeight: 400 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={featureOrderBy === 'Feature'}
                                                    direction={featureOrderBy === 'Feature' ? featureOrder : 'asc'}
                                                    onClick={() => handleFeatureSort('Feature')}
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Feature
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={featureOrderBy === 'Requests'}
                                                    direction={featureOrderBy === 'Requests' ? featureOrder : 'asc'}
                                                    onClick={() => handleFeatureSort('Requests')}
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Requests
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={featureOrderBy === 'TotalTokens'}
                                                    direction={featureOrderBy === 'TotalTokens' ? featureOrder : 'asc'}
                                                    onClick={() => handleFeatureSort('TotalTokens')}
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Total Tokens
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="right">
                                                <TableSortLabel
                                                    active={featureOrderBy === 'CostUsd'}
                                                    direction={featureOrderBy === 'CostUsd' ? featureOrder : 'asc'}
                                                    onClick={() => handleFeatureSort('CostUsd')}
                                                    style={{ fontWeight: 600 }}
                                                >
                                                    Cost
                                                </TableSortLabel>
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sortedFeatures.map((f) => (
                                            <TableRow
                                                key={f.Feature}
                                                hover
                                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                            >
                                                <TableCell sx={{ fontWeight: '500' }}>
                                                    {f.Feature === 'ChartChat' ? 'Chart Chat (RAG)' : f.Feature}
                                                </TableCell>
                                                <TableCell align="right">{f.Requests.toLocaleString()}</TableCell>
                                                <TableCell align="right">{f.TotalTokens.toLocaleString()}</TableCell>
                                                <TableCell align="right">${(f.CostUsd || 0).toFixed(4)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            )}
        </Stack>
    );
};

export default AiUsageAnalyticsPanel;
